import React, { useCallback, useEffect, useMemo, useState } from "react";
import Loader from "../../../Components/Common/Loader";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { SUBDOMAIN } from "../../../assets/meta/meta";
import { useUserStore } from "../../../store/store";
import { RxLockClosed } from "react-icons/rx";
import { FiAlertCircle, FiBell, FiEdit } from "react-icons/fi";
import { RiQuestionLine } from "react-icons/ri";
import { SITE_ID } from "../../../assets/meta/meta";
import qs from "qs";
import InquiryPagination from "./InquiryPagination";
import SearchBox from "./SearchBox";
import InquiryBadgeList from "./InquiryBadgeList";
import { useQuery } from "@tanstack/react-query";
import { inquiryService } from "../../../api/services/inquiry.service";
import { siteService } from "../../../api/services/site.service";

const InquiryListSection = (props) => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const setPage = useCallback((page) => {
    setSearchParams(prev => ({ ...Object.fromEntries(prev), page }));
  }, [setSearchParams]);
  const pageReset = useCallback(() => {
    setSearchParams(prev => ({ ...Object.fromEntries(prev), page: 1 }));
  }, [setSearchParams]);
  const queryString = useMemo(() => {
    return Object.fromEntries(searchParams);
  }, [searchParams]);
  const setQueryString = useCallback((query) => {
    if (typeof query === "function") {
      setSearchParams((prev) => {
        const newQuery = query(Object.fromEntries(prev));
        return newQuery;
      });
    } else {
      setSearchParams(query);
    }
  }, [setSearchParams]);

  const [permission, user] = useUserStore((state) => [state.permission, state.user]);
  const [availableSites, setAvailableSites] = useUserStore((state) => [state.availableSites, state.setAvailableSites]);

  const [inquiryCount, setInquiryCount] = useState(0);
  const [inquiryNext, setInquiryNext] = useState(null);
  const [inquiryPrevious, setInquiryPrevious] = useState(null);

  const [noticeList, setNoticeList] = useState([]);

  const [isAdminMode, setIsAdminMode] = useUserStore((state) => [state.adminInquiry, state.setAdminInquiry]);
  const [adminTargetSubdomain, setAdminTargetSubdomain] = useUserStore((state) => [state.adminTargetSubdomain, state.setAdminTargetSubdomain]);

  const [headBanners, setHeadBanners] = useState(null);
  const [siteName, setSiteName] = useState("");

  const urlQuery = useMemo(() => {
    const queryStrings = {
      ...queryString,
    };
    let _query = qs.stringify(queryStrings);
    return _query;
  }, [queryString]);

  useEffect(() => {
    let _subdomain = SUBDOMAIN;
    if (isAdminMode && adminTargetSubdomain) {
      _subdomain = adminTargetSubdomain;
    } else if (isAdminMode) {
      _subdomain = "";
    } else {
      _subdomain = SUBDOMAIN;
    }
    setQueryString((prev) => {
      return { ...prev, subdomain: _subdomain };
    });
  }, [setQueryString, isAdminMode, adminTargetSubdomain]);

  const fetchInquiryList = async () => {
    let _query = urlQuery;
    if (_query) _query = `?modes=1,2,3,7&${_query}`;

    const response = await inquiryService.getInquiryArticleList(_query);
    const data = response.data;
    setInquiryCount(data.count);
    setInquiryNext(data.next);
    setInquiryPrevious(data.previous);
    return data.results;
  };

  const { data: inquiries, isLoading, isError } = useQuery({
    queryKey: ["inquiryList", queryString, isAdminMode, adminTargetSubdomain],
    queryFn: fetchInquiryList,
  });

  useEffect(() => {
    if (isAdminMode) {
      setNoticeList([]);
      return;
    }

    const fetchArticles = async () => {
      try {
        const _subdomain = SUBDOMAIN;
        const response = await inquiryService.getArticleList(_subdomain, "4,5,6");
        const data = response.data;
        if (data.length === 0) return;
        setNoticeList(data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchArticles();
  }, [isAdminMode]);

  useEffect(() => {
    let _subdomain = window.location.host.split(".")[0];
    if (_subdomain.startsWith("localhost")) _subdomain = "test";
    const fetchHeadBanners = async () => {
      try {
        const response = await inquiryService.getInquiryHeadBanner();
        const data = response.data;
        if (data.datas.length === 0) return;
        const banners = data.datas.filter((item) => item.subdomain === _subdomain);
        if (banners && banners.length) setHeadBanners(banners);
      } catch (error) {
        console.log(error);
      }
    };

    fetchHeadBanners();
  }, []);


  useEffect(() => {
    const fetchSiteData = async () => {
      try {
        const response = await siteService.getSitesApp(SITE_ID);
        const site = response.data.result.sites[0];
        setSiteName(site.name);
      } catch (error) {
        console.log(error);
      }
    };
    fetchSiteData();
  }, []);

  const renderBanner = (headBanner) => {
    if (headBanner.type === "imagebanner") {
      return (
        <a href={headBanner.url} target="_self" rel="noreferrer" className="block transform hover:-translate-y-1 transition-transform duration-300">
          <div className="bg-gradient-to-r from-pink-50 to-white p-8 rounded-2xl shadow-sm border border-pink-100 flex flex-col items-center justify-center text-center">
            <h4 className="text-xl md:text-2xl font-bold text-[#f6475f] mb-2" style={{ fontFamily: "Gmarket-M" }}>{siteName} 리뷰 모아보기</h4>
            <p className="text-gray-600 mb-6 font-medium" style={{ fontFamily: "Gmarket-M" }}>이용 고객들의 후기를 확인해 보세요!</p>
            <div className="flex flex-wrap justify-center gap-3 w-full">
              {headBanner.subitems && headBanner.subitems.length > 1 && (
                <>
                  <a href={headBanner.subitems[0].url} target="_blank" rel="noreferrer" className="w-1/3 max-w-[140px]">
                    <div className="bg-[#f6475f] text-white text-sm font-bold py-2.5 px-4 rounded-xl shadow-md hover:bg-[#e03e52] transition-colors text-center" style={{ fontFamily: "Gmarket-M" }}>
                      리뷰1
                    </div>
                  </a>
                  <a href={headBanner.subitems[1].url} target="_blank" rel="noreferrer" className="w-1/3 max-w-[140px]">
                    <div className="bg-[#f6475f] text-white text-sm font-bold py-2.5 px-4 rounded-xl shadow-md hover:bg-[#e03e52] transition-colors text-center" style={{ fontFamily: "Gmarket-M" }}>
                      리뷰2
                    </div>
                  </a>
                </>
              )}
            </div>
          </div>
        </a>
      );
    }
    return (
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 min-h-[200px] flex flex-col items-center justify-center">
        <div className="flex items-center justify-center mb-6">
          <RiQuestionLine className="text-[#f6475f] text-2xl mr-2" />
          <h5 className="text-lg text-gray-800 m-0 font-bold" style={{ fontFamily: "Gmarket-M" }}>{headBanner.title}</h5>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-4xl">
          {headBanner.subitems && headBanner.subitems.length > 1 && headBanner.subitems.map((item, i) => (
            <a
              key={i}
              href={item.url}
              target="_self"
              className="group block"
            >
              <div className="flex items-center bg-gray-50 hover:bg-white text-gray-700 hover:text-[#f6475f] px-5 py-3.5 rounded-xl border border-transparent hover:border-[#f6475f] transition-all duration-200">
                <span className="font-bold mr-2 text-sm text-gray-400 group-hover:text-[#f6475f]">{i + 1}.</span>
                <span className="font-medium text-sm" style={{ fontFamily: "Gmarket-M" }}>{item.name}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    );
  };

  const InquiryItem = ({ item }) => {
    let _username = item["user_full_name"];
    if (user && item["user_id"] === String(user.id)) {
      // keep as is
    } else {
      _username = _username[0] + "*".repeat(Math.max(0, _username.length - 1));
    }
    const isManager = !!item["user_permission"]?.permission;
    const isNew = (!item["comment_count"] && moment(item["written_date"]) > moment().subtract(1, "days")) ||
      (!!item["latest_comment_date"] && moment(item["latest_comment_date"]) > moment().subtract(1, "days"));

    // Check various conditions for icons
    const showAlert = !!permission?.permission && item["need_extra_info"];
    const showBell = !!permission?.permission && !["4", "5", "6"].includes(item["mode"]) && !item["is_latest_comment_by_staff"];
    const showLock = ["2", "1"].includes(item["mode"]);
    const isNotice = item["is_notice"];

    return (
      <div className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors duration-150">
        <Link to={`/inquiry/detail/${item.id}?${urlQuery}`} className="block px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0 pr-4">
                {showAlert && <FiAlertCircle size="18" className="text-orange-500 shrink-0" />}
                {isNew && <i className="mdi mdi-new-box text-lg text-pink-500 shrink-0"></i>}
                {isNotice && <span className="text-[#f6475f] text-sm font-bold whitespace-nowrap bg-pink-50 px-2 py-0.5 rounded text-xs">[공지]</span>}
                {showBell && <FiBell size="18" className="text-orange-500 shrink-0" />}
                {showLock && <RxLockClosed size="16" className="text-gray-400 shrink-0" />}

                {isAdminMode && <span className="text-[#F6475F] text-xs font-medium px-1.5 py-0.5 bg-red-50 rounded border border-red-100 whitespace-nowrap">{item["site"]}</span>}

                <p className="text-sm font-medium text-gray-900 truncate">
                  {item["title"]}
                </p>

                {!["4", "5", "6"].includes(item["mode"]) && item["comment_count"] > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#f6475f] text-white text-xs font-bold shrink-0 ml-1">
                    {item["comment_count"]}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center shrink-0 gap-4">
              <div className="hidden sm:flex flex-col items-end">
                <p className="text-xs text-gray-500 mb-0.5">
                  {isManager ? <span className="font-bold text-[#f6475f]">관리자</span> : <span>{_username}</span>}
                </p>
                <p className="text-xs text-gray-400">
                  {moment(item["written_date"]).format("YYYY-MM-DD")}
                </p>
              </div>
              <div className="sm:hidden text-xs text-gray-400">
                {moment(item["written_date"]).format("MM.DD")}
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-20 overflow-x-hidden">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">

        {/* Banners */}
        {!!headBanners && headBanners.length > 0 && (
          <div className="space-y-6 mb-8">
            {headBanners.map((banner, i) => (
              <div key={i}>{renderBanner(banner)}</div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Header Section */}
          <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white">
            <div className="w-full">
              <div className="flex items-center justify-between mb-4">
                {/* <h2 className="text-lg font-bold text-gray-800 m-0" style={{fontFamily: "Gmarket-M"}}>문의 내역</h2> */}
                <div className="flex-1"></div> {/* Spacer if we want title */}

                <button
                  onClick={() => {
                    if (user === null) {
                      navigate(`/login?path=/inquiry/form/0`);
                    } else {
                      navigate(`/inquiry/form/${0}`);
                    }
                  }}
                  className="flex items-center justify-center gap-2 bg-[#1e293b] hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm"
                  style={{ fontFamily: "Gmarket-M" }}
                >
                  <FiEdit className="text-sm" />
                  <span>글쓰기</span>
                </button>
              </div>

              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-1 min-w-0">
                  <InquiryBadgeList
                    query={queryString}
                    setQuery={setQueryString}
                    hasPermission={permission?.permission}
                    isTest={SUBDOMAIN === "test"}
                    user={user}
                    availableSites={availableSites}
                    isAdminMode={isAdminMode}
                    setIsAdminMode={setIsAdminMode}
                    adminTargetSubdomain={adminTargetSubdomain}
                    setAdminTargetSubdomain={setAdminTargetSubdomain}
                    pageReset={pageReset}
                  />
                </div>
                <div className="lg:w-80 shrink-0">
                  <SearchBox setQuery={setQueryString} query={queryString} count={inquiryCount} />
                </div>
              </div>
            </div>
          </div>

          {/* List Section */}
          <div className="bg-white min-h-[400px]">
            {/* Table Header (Desktop Only) */}
            <div className="hidden sm:flex bg-gray-50 px-6 py-3 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <div className="flex-1">제목</div>
              <div className="w-24 text-right">작성자</div>
              <div className="w-24 text-right">작성일</div>
            </div>

            {/* Content */}
            {isLoading ? (
              <div className="py-20 flex justify-center">
                <Loader loading={true} />
              </div>
            ) : inquiries && (inquiries.length > 0 || noticeList.length > 0) ? (
              <div className="divide-y divide-gray-100">
                {[...noticeList, ...inquiries].map((item, index) => (
                  <div key={item.id + "_" + index} className="relative group">
                    <InquiryItem item={item} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-20 flex flex-col items-center justify-center text-gray-400">
                <RiQuestionLine size="48" className="mb-4 opacity-50" />
                <p>등록된 게시글이 없습니다.</p>
              </div>
            )}

            {isError && (
              <div className="py-10 text-center text-red-500 text-sm">
                게시글 불러오기를 실패하였습니다. 다시 시도해 주세요.
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <InquiryPagination
              next={inquiryNext}
              prev={inquiryPrevious}
              count={inquiryCount}
              page={page}
              setPage={setPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InquiryListSection;

